export type SavedWorkAreaKey = 'import' | 'export' | 'entity';
export type TariffJurisdiction = 'US' | 'CA';

export interface SavedWorkTypeDefinition {
  key: string;
  label: string;
  docTypes: string[];
  /**
   * Tariff tickets retain the persisted `Tariff` docType. These values provide
   * a narrower Saved Work filter without changing the ticket's document type.
   */
  tariffJurisdictions?: TariffJurisdiction[];
}

export interface SavedWorkAreaDefinition {
  key: SavedWorkAreaKey;
  label: string;
  types: SavedWorkTypeDefinition[];
}

/**
 * User-facing Saved Work taxonomy.
 *
 * Case docType values are persisted and are also used by ticket/PDF renderers,
 * so the UI groups legacy values without rewriting stored cases.
 */
export const SAVED_WORK_AREAS: SavedWorkAreaDefinition[] = [
  {
    key: 'entity',
    label: 'Entity Screen',
    types: [
      {
        key: 'entity-screening',
        label: 'Entity Screening',
        docTypes: [
          'Person / Company',
          'Multiple Entries',
          'API Single - People / Companies',
          'API Bulk - People / Companies',
          'API Document Bulk - People / Companies',
        ],
      },
      // Beneficial Ownership tickets are kept but not listed: the
      // investigation is read on the entity record's Deep Dive instead.
      // {
      //   key: 'beneficial-ownership',
      //   label: 'Beneficial Ownership',
      //   docTypes: ['Beneficial Ownership'],
      // },
      // Re-enable when Supplier Questionnaire should surface in Saved Work.
      // {
      //   key: 'supplier-questionnaire',
      //   label: 'Supplier Questionnaire',
      //   docTypes: ['Supplier Questionnaire'],
      // },
    ],
  },
  {
    key: 'import',
    label: 'Import Compliance',
    types: [
      {
        key: 'import-workflow',
        label: 'Import Workflow',
        docTypes: ['Import Unified'],
      },
      {
        key: 'hts-classification',
        label: 'HTS Classification',
        docTypes: ['Classification'],
      },
      {
        key: 'us-tariff',
        label: 'US Tariff',
        docTypes: ['Tariff'],
        tariffJurisdictions: ['US'],
      },
      {
        key: 'canada-tariff',
        label: 'Canada Tariff',
        docTypes: ['Tariff'],
        tariffJurisdictions: ['CA'],
      },
      {
        key: 'import-product-screening',
        label: 'Import Product Screening',
        docTypes: ['Import Product Screen'],
      },
      // Re-enable when Customs Entry Analysis should surface in Saved Work.
      // {
      //   key: 'customs-entry-analysis',
      //   label: 'Customs Entry Analysis',
      //   docTypes: ['Customs Entry Analysis'],
      // },
    ],
  },
  {
    key: 'export',
    label: 'Export Control',
    types: [
      {
        key: 'export-workflow',
        label: 'Export Workflow',
        docTypes: ['Export Unified'],
      },
      {
        key: 'eccn-check',
        label: 'ECCN Check',
        docTypes: ['ECCN', 'Control Checker', 'ECCN Check'],
      },
      {
        key: 'export-product-screening',
        label: 'Export Product Screening',
        docTypes: ['Export Product Screen'],
      },
      {
        key: 'design-assistant',
        label: 'Design Assistant',
        docTypes: ['Design Assistant'],
      },
    ],
  },
];

export const getSavedWorkArea = (
  key?: string | null,
): SavedWorkAreaDefinition =>
  SAVED_WORK_AREAS.find((area) => area.key === key) || SAVED_WORK_AREAS[0];

export const getSavedWorkDocTypes = (
  area: SavedWorkAreaDefinition,
  selectedTypeKeys: string[],
): string[] => {
  const selectedTypes = area.types.filter((type) =>
    selectedTypeKeys.includes(type.key),
  );
  const typesToUse = selectedTypes.length > 0 ? selectedTypes : area.types;

  return Array.from(
    new Set(
      typesToUse.reduce<string[]>(
        (all, type) => all.concat(type.docTypes),
        [],
      ),
    ),
  );
};

/**
 * Creates the document-type portion of the Mongo query used by Saved Work.
 * The unfiltered Import area includes every persisted tariff jurisdiction;
 * country chips only include tickets that explicitly match that jurisdiction.
 */
export const getSavedWorkTypeFilter = (
  area: SavedWorkAreaDefinition,
  selectedTypeKeys: string[],
): Record<string, any> => {
  const selectedTypes = area.types.filter((type) =>
    selectedTypeKeys.includes(type.key),
  );
  const typesToUse = selectedTypes.length > 0 ? selectedTypes : area.types;
  const includesTariff = typesToUse.some((type) =>
    type.docTypes.includes('Tariff'),
  );
  const standardDocTypes = Array.from(
    new Set(
      typesToUse
        .filter((type) => !type.tariffJurisdictions)
        .reduce<string[]>((all, type) => all.concat(type.docTypes), []),
    ),
  );
  const tariffJurisdictions = Array.from(
    new Set(
      typesToUse.reduce<TariffJurisdiction[]>(
        (all, type) => all.concat(type.tariffJurisdictions || []),
        [],
      ),
    ),
  );

  // All Import Compliance must retain every persisted Tariff ticket,
  // including Mexico records while that country remains hidden as a filter.
  // Once a country-specific chip is selected, use its jurisdiction instead.
  if (selectedTypes.length === 0 && includesTariff) {
    if (standardDocTypes.length === 0) return { docType: 'Tariff' };
    return {
      $or: [{ docType: { $in: standardDocTypes } }, { docType: 'Tariff' }],
    };
  }

  if (tariffJurisdictions.length === 0) {
    return { docType: { $in: standardDocTypes } };
  }

  const tariffFilter = {
    docType: 'Tariff',
    tariffJurisdiction: { $in: tariffJurisdictions },
  };
  if (standardDocTypes.length === 0) {
    return tariffFilter;
  }

  return {
    $or: [{ docType: { $in: standardDocTypes } }, tariffFilter],
  };
};

/** Accept current keys plus old URL values (labels or raw docTypes). */
export const normalizeSavedWorkTypeKeys = (
  area: SavedWorkAreaDefinition,
  values: string[],
): string[] =>
  Array.from(
    new Set(
      values
        .map((value) => {
          const matchingType = area.types.find(
            (type) =>
              type.key === value ||
              type.label === value ||
              type.docTypes.includes(value),
          );
          return matchingType?.key;
        })
        .filter((value): value is string => !!value),
    ),
  );

export const getSavedWorkTypeLabel = (docType?: string): string => {
  if (!docType) return '';

  for (const area of SAVED_WORK_AREAS) {
    const type = area.types.find((item) => item.docTypes.includes(docType));
    if (type) return type.label;
  }

  return docType;
};

export const getSavedWorkTicketTypeLabel = (ticket?: {
  docType?: string;
  tariffJurisdiction?: string;
}): string => {
  if (ticket?.docType === 'Tariff') {
    if (ticket.tariffJurisdiction === 'US') return 'US Tariff';
    if (ticket.tariffJurisdiction === 'CA') return 'Canada Tariff';
    if (ticket.tariffJurisdiction === 'MX') return 'Mexico Tariff';
    // Legacy tickets are backfilled to US. Keep this fallback so a ticket
    // created during deployment is not surfaced under a retired label.
    return 'US Tariff';
  }

  return getSavedWorkTypeLabel(ticket?.docType);
};
